import { Formik, FormikProps } from 'formik';
import React, { memo, useRef, useState } from 'react';
import * as Yup from 'yup';
import { SendNotificationResponse, upsertTextingOptIn, useSendLostQrCode } from '../../API/GoMoto';
import { CTAButton } from '../../controls/ButtonBase';
import { useLocalization } from '../../context/Localization/localization';
import { Heading } from '../../controls/Heading/Heading';
import { Spinner } from '../../controls/Spinner/Spinner';
import { useFullReset, useKioskState } from '../../context/Kiosk/kiosk';
import { nameof } from '../../helpers/nameof';
import { GMFormPhoneOrEmailField } from '../../controls/TextField';
import { GMButton } from '../../controls/Button';
import { GMDialog, GMDialogInfo, GMDialogTryAgainOrOk, GMDialogYesNo } from '../../controls/Dialog';
import { GMSizedIcon } from '../../controls/Icon';
import { GMForm, GMFormWrapper } from '../../controls/Form';
import { SendNotificationResult } from '../../API/Enums';
import { formatAsPhoneNumber } from '../../helpers/Number';
import { GMGrid } from '../../controls/Grid';
import css from './ResendQRCode.module.scss';

// TODO: This is effectively a common control at this point, we should move it there

export interface IProps {
    onProceed: () => void;
}

interface FormValues {
    phoneOrEmail: string;
    fallbackToEmail: boolean;
}

export const ResendQRCodeForm: React.FC<IProps> = memo(({ onProceed }) => {
    const Localization = useLocalization();
    const resend = useSendLostQrCode();
    const [error, setError] = useState<boolean>(false);
    const reset = useFullReset();
    const [resendBox, setResendBox] = useState<SendNotificationResult.EmailSent | SendNotificationResult.TextSent | SendNotificationResult.EmailAndTextSent | undefined>(undefined);
    const [showOptInModal, setShowOptInModal] = useState<boolean>(false);
    const [showOptedOutModal, setShowOptedOutModal] = useState<boolean>(false);
    const { key: kioskKey } = useKioskState();
    const formikRef = useRef<FormikProps<FormValues> | null>(null);

    // Data needed for opt-in/out modals
    const [appointmentKey, setAppointmentKey] = useState<string>('');
    const [dealerKey, setDealerKey] = useState<string>('');
    const [dealerName, setDealerName] = useState<string>('');
    const [dealerTextNumber, setDealerTextNumber] = useState<string>('');

    const initialValues: FormValues = {
        phoneOrEmail: '',
        fallbackToEmail: false,
    };

    return (
        <>
            <Formik
                initialValues={initialValues}
                innerRef={formikRef}
                onSubmit={async (values: FormValues) => {
                    setAppointmentKey('');
                    setDealerKey('');
                    setDealerName('');
                    setDealerTextNumber('');

                    values.phoneOrEmail = values.phoneOrEmail.trim();

                    const isPhone: boolean = !(/[^0-9]/).test(values.phoneOrEmail);
                    const response: SendNotificationResponse = await resend({
                        kioskKey: kioskKey,
                        phone: isPhone ? values.phoneOrEmail : undefined,
                        email: !isPhone ? values.phoneOrEmail : undefined,
                        sendText: isPhone,
                        sendEmail: !isPhone,
                        fallbackToEmail: values.fallbackToEmail,
                    });

                    switch (response.result) {
                        case SendNotificationResult.EmailSent:
                        case SendNotificationResult.TextSent:
                        case SendNotificationResult.EmailAndTextSent:
                            setResendBox(response.result);
                            break;
                        case SendNotificationResult.NotOptedInForTexts:
                            if (response.appointmentKey && response.dealerKey && response.dealerName) {
                                setAppointmentKey(response.appointmentKey);
                                setDealerKey(response.dealerKey);
                                setDealerName(response.dealerName);

                                setShowOptInModal(true);
                            }
                            else {
                                setError(true);
                            }
                            break;
                        case SendNotificationResult.OptedOutForTexts:
                            if (response.dealerTextNumber) {
                                setDealerTextNumber(response.dealerTextNumber);

                                setShowOptedOutModal(true);
                            }
                            else {
                                setError(true);
                            }
                            break;
                        case SendNotificationResult.AppointmentNotFound:
                        case SendNotificationResult.UnknownQRCode:
                        case SendNotificationResult.FailedToSend:
                            setError(true);
                            break;
                    }
                }}
                validationSchema={Yup.object()
                    .shape({
                        [nameof<FormValues>('phoneOrEmail')]: Yup.lazy((val) => {
                            return !(/[^0-9]/).test(val as string) ?
                                Yup.string()
                                    .trim()
                                    .phone(Localization.EmailOrPhoneInvalid)
                                    .nullable() :
                                Yup.string()
                                    .trim()
                                    .email(Localization.EmailOrPhoneInvalid)
                                    .nullable();
                        },
                        ),
                    })}
            >
                {({ isSubmitting, setFieldValue }) => {
                    return (
                        <GMForm
                            submitButton={
                                <CTAButton
                                    type={'submit'}
                                    styleType={'primary'}
                                    kioskOptions={{ inlineOptions: { sizedIcon: <GMSizedIcon iconSize={'custom'} iconType={'Send'} /> } }}
                                    title={Localization.ClickToSendQRCode}
                                    label={Localization.SendQR}
                                    id={Localization.ClickToSendQRCode}
                                    ariaLabel={Localization.ClickToSendQRCode}
                                />
                            }
                            heading={
                                <Heading
                                    primaryHeading={Localization.LostYourQRCode}
                                    secondaryHeading={Localization.EnterYourPhoneNumberOrEmailAddressAndAQRCode}
                                    showMustache
                                />
                            }
                        >
                            <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                            <GMFormPhoneOrEmailField
                                name={nameof<FormValues>('phoneOrEmail')}
                                id={Localization.PhoneNumberOrEmail}
                                title={Localization.PhoneNumberOrEmail}
                                label={Localization.PhoneNumberOrEmail}
                                ariaLabel={Localization.PhoneNumberOrEmail}
                                autoFocus
                                required
                                disabled={isSubmitting}
                                onChangeCallback={() => {
                                    setFieldValue(nameof<FormValues>('fallbackToEmail'), false);
                                }}
                            />
                            {isSubmitting && <Spinner />}
                        </GMForm>
                    );
                }}
            </Formik>
            <GMDialog
                ariaLabel={resendBox === SendNotificationResult.TextSent ? Localization.QRCodeTextResent : Localization.QRCodeEmailResent}
                open={resendBox !== undefined}
                onClose={() => setResendBox(undefined)}
                title={resendBox === SendNotificationResult.TextSent ? Localization.TextSent : Localization.EmailSent}
                actions={[
                    <GMButton
                        id={Localization.OK}
                        title={Localization.OK}
                        ariaLabel={Localization.OK}
                        onClick={onProceed}
                    >
                        {Localization.OK}
                    </GMButton>,
                ]}
            >
                {resendBox === SendNotificationResult.TextSent ? Localization.QRCodeTextResent : Localization.QRCodeEmailResent}
            </GMDialog>

            <GMDialogTryAgainOrOk
                ariaLabel={Localization.OurRecordsShowNoQrCode}
                open={!!error}
                title={Localization.NoMatchFound}
                onTryAgain={() => setError(false)}
                onOk={reset}
            >
                {Localization.OurRecordsShowNoQrCode}
            </GMDialogTryAgainOrOk>


            <GMDialogYesNo
                title={Localization.OptInForTexts}
                ariaLabel={Localization.OptInForTexts}
                className={css.dialogTitle}
                paperDialogClasses={css.dialog}
                open={showOptInModal}
                onNo={async () => {
                    setShowOptInModal(false);

                    if (formikRef.current) {
                        formikRef.current.setSubmitting(true);
                        await formikRef.current.setFieldValue(nameof<FormValues>('fallbackToEmail'), true);

                        await upsertTextingOptIn(appointmentKey, formikRef.current.values.phoneOrEmail.trim(), false, dealerKey);

                        formikRef.current.submitForm();
                    }
                }}
                onYes={async () => {
                    setShowOptInModal(false);

                    if (formikRef.current) {
                        formikRef.current.setSubmitting(true);
                        await formikRef.current.setFieldValue(nameof<FormValues>('fallbackToEmail'), true);

                        await upsertTextingOptIn(appointmentKey, formikRef.current.values.phoneOrEmail.trim(), true, dealerKey);

                        formikRef.current.submitForm();
                    }
                }}
            >
                {Localization.formatString(Localization.ByClickingYesYouAgreeToReceiveTextMessages, dealerName)}
                <br />
                <br />
                {Localization.FrequencyVaries}
            </GMDialogYesNo>

            {/* TODO break out into common component between this and appointment details */}
            <GMDialogInfo
                ariaLabel={Localization.WeMissYou}
                ariaDescribedBy={Localization.WeMissYou}
                className={css.dialogTitle}
                paperDialogClasses={css.dialog}
                title={Localization.WeMissYou}
                onClose={async () => {
                    setShowOptedOutModal(false);

                    if (formikRef.current) {
                        await formikRef.current.setFieldValue(nameof<FormValues>('fallbackToEmail'), true);
                        formikRef.current.submitForm();
                    }
                }}
                closeButtonText={Localization.OK}
                open={showOptedOutModal}
            >
                <GMGrid container id={Localization.WeMissYou}>
                    <GMGrid className={css.dialogBody}>
                        {Localization.OhNoItLooksLikeYouOptedOutOfTextMessaging}
                    </GMGrid>
                    <GMGrid className={css.dialogBody}>
                        {Localization.formatString(Localization.TextStartTo, formatAsPhoneNumber(dealerTextNumber))}
                    </GMGrid>
                </GMGrid>
            </GMDialogInfo>
        </>
    );
});